import type { ComponentConfig } from '@measured/puck';
import { ArrowUpRight, Calculator, FileBarChart, GitCompareArrows, Hammer } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { StaggerGrid, StaggerItem } from '../motion/StaggerGrid';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/cn';

interface BentoServicesProps {
  eyebrow?: string;
  title?: string;
  description?: string;
}

interface BentoEntry {
  href: string;
  badge: string;
  title: string;
  description: string;
  icon: LucideIcon;
  features: string[];
  span: string;
  bg: string;
  ring: string;
  iconBg: string;
}

const ENTRIES: BentoEntry[] = [
  {
    href: '/cee',
    badge: 'CEE',
    title: 'Primes Certificats d’Économies d’Énergie',
    description:
      'On monte le dossier de A à Z et on optimise le montant de votre prime énergie. Pour PME, ETI, industrie, agriculture et copropriété.',
    icon: Calculator,
    features: ['Locaux tertiaires', 'Sites industriels', 'Exploitations agricoles'],
    span: 'lg:col-span-3 lg:row-span-2',
    bg: 'bg-gradient-to-br from-brand-50 via-paper to-ivory-100',
    ring: 'ring-brand-200/40',
    iconBg: 'bg-brand-100 text-brand-700',
  },
  {
    href: '/comparateur',
    badge: 'Courtage',
    title: 'Comparateur électricité & gaz',
    description:
      "Tous les fournisseurs accessibles à votre profil, comparés par un courtier indépendant des fournisseurs d'énergie. Négociation des contrats, optimisation des frais d'acheminement et de la taxe électricité selon votre secteur.",
    icon: GitCompareArrows,
    features: ['Multi-fournisseurs', 'Optimisation TURPE', 'Remboursement CSPE'],
    span: 'lg:col-span-2',
    bg: 'bg-gradient-to-br from-ivory-50 to-brand-50/40',
    ring: 'ring-stone-200/60',
    iconBg: 'bg-brand-100 text-brand-600',
  },
  {
    href: '/renovation',
    badge: 'Aides',
    title: 'Aides cumulées rénovation',
    description: 'MaPrimeRénov’, CEE, éco-PTZ, aides locales — calculées ensemble.',
    icon: Hammer,
    features: ['Particuliers', 'Copropriétés', 'Artisans RGE'],
    span: 'lg:col-span-2',
    bg: 'bg-gradient-to-br from-accent-50 to-ivory-100',
    ring: 'ring-accent-200/40',
    iconBg: 'bg-accent-100 text-accent-700',
  },
  {
    href: '/decret-tertiaire',
    badge: 'Conformité',
    title: 'Décret Tertiaire — objectif 2030',
    description:
      'Audit, plan d’actions, déclaration OPERAT annuelle, suivi pluriannuel. Sans sanction.',
    icon: FileBarChart,
    features: ['ETI & grands comptes', 'Patrimoine ≥ 1000 m²', 'OPERAT 30/09'],
    span: 'lg:col-span-3',
    bg: 'bg-gradient-to-br from-stone-50 via-paper to-brand-50',
    ring: 'ring-stone-200/60',
    iconBg: 'bg-stone-100 text-stone-700',
  },
];

export function BentoServices({
  eyebrow = 'Notre métier',
  title = 'Quatre univers, un seul interlocuteur.',
  description = 'On compare, on estime, on monte les dossiers. Sans frais cachés, sans engagement, en s’appuyant uniquement sur les fiches CEE en vigueur et les sources officielles.',
}: BentoServicesProps) {
  return (
    <section className="bg-paper py-20 md:py-28">
      <Container>
        <Reveal>
          <div className="mb-12 max-w-2xl md:mb-16">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="mt-4 text-balance font-display text-display-3 font-bold tracking-tight text-ink">
              {title}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-stone-600">{description}</p>
          </div>
        </Reveal>

        <StaggerGrid className="grid grid-cols-1 gap-4 md:gap-5 lg:auto-rows-[minmax(220px,auto)] lg:grid-cols-5">
          {ENTRIES.map((e) => {
            const Icon = e.icon;
            return (
              <StaggerItem key={e.href} className={e.span}>
                <a
                  href={e.href}
                  className={cn(
                    'group relative flex h-full flex-col justify-between overflow-hidden rounded-3xl p-7 ring-1 transition-all duration-450 ease-stripe hover:-translate-y-1 hover:shadow-floating md:p-8',
                    e.bg,
                    e.ring,
                  )}
                >
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <div
                        className={cn(
                          'flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-450 ease-stripe group-hover:rotate-3 group-hover:scale-105',
                          e.iconBg,
                        )}
                      >
                        <Icon className="h-6 w-6" strokeWidth={1.6} />
                      </div>
                      <Badge variant="ivory" size="sm">
                        {e.badge}
                      </Badge>
                    </div>
                    <h3
                      className="mt-6 font-display text-2xl font-semibold leading-tight tracking-tight text-ink"
                      dangerouslySetInnerHTML={{ __html: e.title }}
                    />
                    <p className="mt-3 max-w-prose text-stone-600">{e.description}</p>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      {e.features.map((f) => (
                        <span
                          key={f}
                          className="rounded-full bg-surface/70 px-2.5 py-0.5 text-[11px] font-medium text-stone-600 ring-1 ring-inset ring-stone-200/60 backdrop-blur"
                          dangerouslySetInnerHTML={{ __html: f }}
                        />
                      ))}
                    </div>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface text-ink ring-1 ring-stone-200/60 transition-all duration-350 ease-stripe group-hover:rotate-[-12deg] group-hover:bg-ink group-hover:text-ivory-50">
                      <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                    </span>
                  </div>
                </a>
              </StaggerItem>
            );
          })}
        </StaggerGrid>
      </Container>
    </section>
  );
}

export const BentoServicesConfig: ComponentConfig<BentoServicesProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    description: { type: 'textarea' },
  },
  defaultProps: {
    eyebrow: 'Notre métier',
    title: 'Quatre univers, un seul interlocuteur.',
    description:
      'On compare, on estime, on monte les dossiers. Sans frais cachés, sans engagement, en s’appuyant uniquement sur les fiches CEE en vigueur et les sources ADEME / INSEE.',
  },
  render: BentoServices,
};
